import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, CheckCircle2, Star, TrendingUp, Banknote, Users, Cog, BarChart3, FileText, Zap, Shield, Building2, MailX, UsersRound, EyeOff } from 'lucide-react';
import { pillars, completePack, testimonials, stats, faqs } from '../data/mock';
import './HomePage.css';

const HomePage = () => {
  const observerRef = useRef(null);
  const [activeTestimonial, setActiveTestimonial] = useState(null);
  const [carouselIndex, setCarouselIndex] = useState(0);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => observerRef.current?.observe(el));

    return () => observerRef.current?.disconnect();
  }, []);

  const iconMap = { Users, Cog, BarChart3, FileText, Shield };

  const prevTestimonial = () => {
    setCarouselIndex((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1));
  };

  const nextTestimonial = () => {
    setCarouselIndex((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1));
  };

  return (
    <div className="home-page">

      {/* Hero Section */}
      <section className="hero-section hero-centered">
        <div className="hero-background">
          <div className="hero-gradient" />
        </div>
        <div className="section-container">
          <div className="hero-center animate-on-scroll">
            <div className="hero-badge">Structuration opérationnelle</div>
            <h1 className="hero-title">
              Structurez votre entreprise.<br />
              <span className="gold-accent">Libérez votre croissance.</span>
            </h1>
            <p className="hero-subtitle hero-subtitle-centered">
              SYNA transforme le chaos opérationnel en systèmes fluides et efficaces.
              Canal client, équipe, pilotage, facturation — on structure tout pour que
              vous puissiez vous concentrer sur l'essentiel.
            </p>
            <div className="hero-cta hero-cta-centered">
              <Link to="/contact" className="btn btn-primary btn-large">
                Diagnostic gratuit <ArrowRight size={20} />
              </Link>
              <Link to="#piliers" className="btn btn-secondary btn-large">
                Découvrir les piliers
              </Link>
            </div>
            <div className="hero-proof hero-proof-centered">
              <span className="proof-item">
                <CheckCircle2 size={16} className="proof-icon" /> Sans engagement
              </span>
              <span className="proof-item">
                <CheckCircle2 size={16} className="proof-icon" /> Résultats en 90 jours
              </span>
              <span className="proof-item">
                <CheckCircle2 size={16} className="proof-icon" /> Adapté à votre secteur
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="section-padding">
        <div className="section-container">
          <div className="problem-section animate-on-scroll">
            <h2 className="section-title">Vous reconnaissez votre situation ?</h2>
            <div className="problems-grid">
              <div className="problem-card">
                <div className="problem-icon problem-icon--red">
                  <MailX size={32} />
                </div>
                <h3>Demandes qui se perdent</h3>
                <p>Emails oubliés, appels non retournés, prospects qui disparaissent</p>
              </div>
              <div className="problem-card">
                <div className="problem-icon problem-icon--orange">
                  <UsersRound size={32} />
                </div>
                <h3>Équipe qui rame</h3>
                <p>Qui fait quoi ? Tâches en double, infos mal transmises, temps perdu</p>
              </div>
              <div className="problem-card">
                <div className="problem-icon problem-icon--purple">
                  <EyeOff size={32} />
                </div>
                <h3>Aucune visibilité</h3>
                <p>Impossible de savoir où vous en êtes vraiment sur vos performances</p>
              </div>
              <div className="problem-card">
                  <div className="problem-icon problem-icon--pink">
                  <Banknote size={32} />
                </div>
                <h3>Trésorerie tendue</h3>
                <p>Factures en retard, relances épuisantes, paiements qui traînent</p>
              </div>
            </div>
            <p className="problem-conclusion">
              <strong className="gold-accent">Le coût caché ?</strong> Des dizaines de milliers d'euros
              perdus chaque année en opportunités manquées, temps gaspillé et stress permanent.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="section-container">
          <div className="stats-grid animate-on-scroll">
            {stats.map((stat, index) => (
              <div key={index} className="stat-card">
                <div className="stat-value">{stat.value}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pillars Section */}
      <section className="section-padding" id="piliers">
        <div className="section-container">
          <div className="section-header animate-on-scroll">
            <h2 className="section-title">Les 5 piliers de votre transformation</h2>
            <p className="section-description">
              Une méthode éprouvée pour structurer votre entreprise de bout en bout
            </p>
          </div>
          <div className="pillars-list">
            {pillars.map((pillar, index) => {
              const Icon = iconMap[pillar.icon];
              return (
                <div
                  key={pillar.id}
                  className={`pillar-card animate-on-scroll ${index % 2 === 0 ? 'left' : 'right'}`}
                  id={pillar.title.toLowerCase().replace(/\s+/g, '-')}
                >
                  <div className="pillar-content">
                    <div className="pillar-number">{pillar.number}</div>
                    <div className="pillar-text">
                      <h3 className="pillar-title">{pillar.title}</h3>
                      <p className="pillar-subtitle">{pillar.subtitle}</p>
                      <p className="pillar-description">{pillar.description}</p>
                      <Link
                        to={`/contact?subject=${encodeURIComponent(pillar.title.toLowerCase().replace(/\s+/g, '-'))}`}
                        className="btn btn-secondary pillar-cta"
                      >
                        Prendre rendez-vous
                      </Link>
                    </div>
                  </div>
                  <div className="pillar-visual">
                    <div className="pillar-icon"><Icon size={40} /></div>
                    <img src={pillar.image} alt={pillar.title} className="pillar-img" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Complete Pack Section */}
      <section className="pack-section" id="pack-360">
        <div className="section-container">
          <div className="pack-card animate-on-scroll">
            <div className="pack-content">
              <div className="pack-badge">Le pack complet</div>
              <h2 className="pack-title">SYNA 360°</h2>
              <p className="pack-description">{completePack.description}</p>
              <div className="pack-bonus">
                <Zap size={20} />
                <span>+ Offre E-Facturation : {completePack.bonus}</span>
              </div>
              <Link to="/contact" className="btn btn-primary btn-large">
                Demander un devis <ArrowRight size={20} />
              </Link>
            </div>
            <div className="pack-image">
              <img src={completePack.image} alt="SYNA 360" />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section-padding testimonials-section">
        <div className="section-container">
          <div className="section-header animate-on-scroll">
            <h2 className="section-title">Ils ont structuré avec SYNA</h2>
            <p className="section-description">Des résultats concrets, mesurables</p>
          </div>

          <div className="carousel-wrapper">
            <button className="carousel-arrow carousel-arrow-left" onClick={prevTestimonial} aria-label="Précédent">
              <ArrowLeft size={22} />
            </button>

            <div className="carousel-viewport">
              {testimonials.map((testimonial, index) => {
                const total = testimonials.length;
                const prev = (carouselIndex - 1 + total) % total;
                const next = (carouselIndex + 1) % total;
                let position = 'hidden';
                if (index === carouselIndex) position = 'active';
                else if (index === prev) position = 'prev';
                else if (index === next) position = 'next';

                return (
                  <div
                    key={testimonial.id}
                    className={`testimonial-card carousel-card carousel-card--${position}`}
                    onClick={() => index === carouselIndex && setActiveTestimonial(testimonial)}
                  >
                    <div className="testimonial-header">
                      <div className="testimonial-company-icon">
                        <Building2 size={28} />
                      </div>
                      <div className="testimonial-info">
                        <h4>{testimonial.company}</h4>
                        <p className="testimonial-sector">{testimonial.sector}</p>
                      </div>
                      <div className="testimonial-rating">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} size={16} fill="var(--gold)" color="var(--gold)" />
                        ))}
                      </div>
                    </div>
                    <blockquote className="testimonial-quote">"{testimonial.quote}"</blockquote>
                    <div className="testimonial-results">
                      <TrendingUp size={18} />
                      <span>{testimonial.results}</span>
                    </div>
                    {index === carouselIndex && (
                      <div className="carousel-card-hint">Cliquer pour agrandir</div>
                    )}
                  </div>
                );
              })}
            </div>

            <button className="carousel-arrow carousel-arrow-right" onClick={nextTestimonial} aria-label="Suivant">
              <ArrowRight size={22} />
            </button>
          </div>

          <div className="carousel-dots">
            {testimonials.map((_, index) => (
              <button
                key={index}
                className={`carousel-dot ${index === carouselIndex ? 'active' : ''}`}
                onClick={() => setCarouselIndex(index)}
                aria-label={`Témoignage ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Modal */}
      {activeTestimonial && (
        <div className="testimonial-modal-overlay" onClick={() => setActiveTestimonial(null)}>
          <div className="testimonial-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setActiveTestimonial(null)}>✕</button>
            <div className="modal-header">
              <div className="testimonial-company-icon modal-icon">
                <Building2 size={36} />
              </div>
              <div>
                <h3>{activeTestimonial.company}</h3>
                <p className="testimonial-sector">{activeTestimonial.sector}</p>
                <div className="testimonial-rating" style={{ marginTop: '0.5rem' }}>
                  {[...Array(activeTestimonial.rating)].map((_, i) => (
                    <Star key={i} size={18} fill="var(--gold)" color="var(--gold)" />
                  ))}
                </div>
              </div>
            </div>
            <blockquote className="modal-quote">"{activeTestimonial.quote}"</blockquote>
            <div className="testimonial-results modal-results">
              <TrendingUp size={20} />
              <span>{activeTestimonial.results}</span>
            </div>
          </div>
        </div>
      )}

      {/* FAQ Section */}
      <section className="section-padding faq-section">
        <div className="section-container">
          <div className="section-header animate-on-scroll">
            <h2 className="section-title">Questions fréquentes</h2>
          </div>
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <details key={index} className="faq-item animate-on-scroll">
                <summary className="faq-question">{faq.question}</summary>
                <p className="faq-answer">{faq.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="cta-section">
        <div className="section-container">
          <div className="cta-content animate-on-scroll">
            <h2 className="cta-title">Prêt à structurer votre croissance ?</h2>
            <p className="cta-text">
              Commencez par un diagnostic gratuit de 30 minutes.
              On identifie ensemble vos points de friction et le plan d'action.
            </p>
            <Link to="/contact" className="btn btn-primary btn-large">
              Réserver mon diagnostic gratuit <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
};

export default HomePage;